import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, Bell, Package, ClipboardList, Truck, Calendar,
  AlertCircle, Zap
} from 'lucide-react';

const PlanningForecastingTab = () => {
  const [timeRange, setTimeRange] = useState('month');

  const consumptionAlerts = [
    { customer: 'Gulf Distributors', product: 'Item A', pattern: '~50 boxes / 30 days', nextOrder: '~5 days', action: 'Call for restock' },
    { customer: 'Metro Retail', product: 'Item B', pattern: '~200 units / 14 days', nextOrder: '~2 days', action: 'Call for restock' },
    { customer: 'Saudi Foods Co', product: 'Item C', pattern: '~100 units / 21 days', nextOrder: '~12 days', action: 'Follow up' },
  ];

  const demandForecast = [
    { sku: 'SKU-8821', name: 'Premium Olive Oil 5L', current: 1200, forecastNext: 1800, suggestedPR: 600 },
    { sku: 'SKU-7701', name: 'Tomato Paste 1kg', current: 200, forecastNext: 1500, suggestedPR: 1300 },
    { sku: 'SKU-5500', name: 'Rice Basmati 25kg', current: 3500, forecastNext: 4200, suggestedPR: 700 },
  ];

  const draftPRs = [
    { id: 'PR-DRAFT-001', sku: 'SKU-7701', qty: 1300, reason: 'Demand forecast – stock out next month', pushed: false },
    { id: 'PR-DRAFT-002', sku: 'SKU-8821', qty: 600, reason: 'November demand spike (5K units historical)', pushed: true },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl font-semibold text-gray-900">Planning & Forecasting</h2>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
        >
          <option value="week">Next 7 days</option>
          <option value="month">Next 30 days</option>
          <option value="quarter">Next quarter</option>
        </select>
      </div>

      {/* Consumption AI Alerts */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex items-center gap-2">
          <Bell className="w-5 h-5 text-teal-600" />
          <h3 className="text-lg font-semibold text-gray-900">Consumption AI alerts</h3>
        </div>
        <p className="px-6 pt-2 text-sm text-gray-600">
          The system learns buying rhythm (e.g. 50 boxes every 30 days) and alerts the rep to call around Day 25 for restock.
        </p>
        <div className="p-6">
          <div className="space-y-4">
            {consumptionAlerts.map((a, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.01 }}
                className="flex flex-wrap items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-teal-200"
              >
                <div>
                  <p className="font-medium text-gray-900">{a.customer} – {a.product}</p>
                  <p className="text-sm text-gray-500">Pattern: {a.pattern} · Next order in {a.nextOrder}</p>
                </div>
                <span className="px-3 py-1.5 bg-teal-100 text-teal-800 text-sm font-medium rounded-lg">{a.action}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Demand forecasting */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Demand forecasting</h3>
          <p className="text-sm text-gray-500 mt-1">Aggregates historical sales to predict what you will need to sell; supports proactive planning.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current stock</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Forecast (next period)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Suggested PR qty</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {demandForecast.map((row) => (
                <tr key={row.sku} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{row.sku}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{row.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{row.current.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{row.forecastNext.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm font-medium text-teal-700">{row.suggestedPR.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Automated PRs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-600" />
          <h3 className="text-lg font-semibold text-gray-900">Automated purchase requests (PRs)</h3>
        </div>
        <p className="px-6 pt-2 text-sm text-gray-600">
          If the system predicts a stock-out, it drafts a PR and can push it to the Supply Chain module for procurement to create a PO.
        </p>
        <div className="p-6">
          <div className="space-y-3">
            {draftPRs.map((pr) => (
              <div key={pr.id} className="flex flex-wrap items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{pr.id}</p>
                  <p className="text-sm text-gray-600">{pr.sku} · Qty {pr.qty.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">{pr.reason}</p>
                </div>
                {pr.pushed ? (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Pushed to Supply Chain</span>
                ) : (
                  <button className="px-3 py-1.5 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700">
                    Push to Supply Chain
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanningForecastingTab;
